/* ==================== FÆLLES LYD-GATE ========================================
   Alle boder synther selv deres lyde (Web Audio-oscillatorer, kopieret pr.
   komponent — bevidst konvention), men de deler ÉN AudioContext med én
   master-gain her. Det giver ét kontrolpunkt til ads-kravet "al spillyd
   muted mens en annonce vises" (roadmap 1.3) — og browsere har alligevel
   loft over antal samtidige AudioContexts.

   Brug i en bod:
     import { ensureCtx, out } from "../audio.js";
     const ensure = () => ensureCtx();
     ... g.connect(out()); ...        // i stedet for g.connect(c.destination)
================================================================================ */

let ctx = null;
let master = null;
let muted = false;

/** Opret/genoptag den fælles AudioContext. Returnerer null hvis Web Audio
    ikke findes (lyd er altid valgfri i appen). */
export function ensureCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      master = ctx.createGain();
      master.gain.value = muted ? 0 : 1;
      master.connect(ctx.destination);
    } catch (e) { /* ingen lyd-understøttelse */ }
  }
  if (ctx && ctx.state === "suspended") ctx.resume();
  try { if (window.speechSynthesis) { window.speechSynthesis.getVoices(); } } catch (e) { /* varm stemmelisten op (loader asynkront) */ }
  return ctx;
}

/** Master-gain-noden alle lyde skal connectes til (findes når ensureCtx()
    har returneret en context). */
export function out() {
  return master;
}

/** Mutes øjeblikkeligt (kort rampe mod klik) — kaldes af ads.js beforeAd. */
export function muteAll() {
  muted = true;
  if (ctx && master) master.gain.setTargetAtTime(0, ctx.currentTime, 0.02);
  try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* valgfri */ }
}

/** Genåbner lyden — kaldes af ads.js afterAd. */
export function unmuteAll() {
  muted = false;
  if (ctx && master) master.gain.setTargetAtTime(1, ctx.currentTime, 0.05);
}

export function isMuted() {
  return muted;
}

/* ---- kommentator-stemme (Web Speech API — ingen lydfiler, gratis i browseren).
   Respekterer ads-gaten: taler ikke mens muted, og muteAll() cancel'er igangværende
   tale. speechSynthesis går UDENOM master-gain, derfor den manuelle gate.
   Speakeren er ALTID engelsk mandestemme (Daniels valg 18/7) — sprog-invariant
   ligesom TeddyChase.com-banneret. */
const MALE_HINTS = ["guy", "ryan", "david", "mark", "daniel", "george", "james", "christopher", "eric", "brian", "matthew", "male"];
let maleVoice = null;

function pickMaleEnVoice() {
  if (maleVoice) return maleVoice;
  try {
    const vs = window.speechSynthesis.getVoices().filter((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
    if (!vs.length) return null;         // stemmelisten loader asynkront — prøv igen næste gang
    maleVoice = vs.find((v) => MALE_HINTS.some((h) => v.name.toLowerCase().includes(h))) || vs[0];
  } catch (e) { /* valgfri */ }
  return maleVoice;
}

export function speak(text, { rate = 1.3, pitch = 1.1, interrupt = false } = {}) {
  if (muted) return;
  try {
    const s = window.speechSynthesis;
    if (!s) return;                       // tale er altid valgfri
    if (interrupt) s.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-GB";
    const v = pickMaleEnVoice();
    if (v) { u.voice = v; u.lang = v.lang; }
    u.rate = rate;
    u.pitch = pitch;
    s.speak(u);
  } catch (e) { /* ingen tale-understøttelse */ }
}
